import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { Calendar, Clock, User, Briefcase, FileText, X, AlertCircle, Loader2 } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import MultiServiceSelector from '../../../components/MultiServiceSelector';

const AppointmentModal = ({
  isOpen,
  onClose,
  onSave,
  isEditing,
  appointmentForm,
  setAppointmentForm,
  customers,
  employees,
  services,
  selectedServices,
  setSelectedServices,
  isSaving = false,
}) => {
  const { t } = useTranslation('appointments');
  const [timeError, setTimeError] = useState('');

  const validateTimeSlot = (time) => {
    setTimeError('');
    if (!time) { setTimeError(t('modal.timeError')); return false; }
    return true;
  };

  const handleTimeChange = (time) => {
    if (validateTimeSlot(time)) {
      setAppointmentForm((prev) => ({ ...prev, time }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        content: { position: 'relative', top: 'auto', left: 'auto', right: 'auto', bottom: 'auto', maxWidth: '600px', width: '90%', maxHeight: '90vh', margin: '0 auto', padding: 0, border: 'none', background: 'transparent', overflow: 'visible' },
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">
            {isEditing ? t('modal.titleEdit') : t('modal.titleNew')}
          </h2>
          <button onClick={onClose} disabled={isSaving} className="text-white hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="h-4 w-4 text-blue-500" />
              {t('modal.dateLabel')}
            </label>
            <input type="date" value={appointmentForm.date}
              onChange={(e) => setAppointmentForm((prev) => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Clock className="h-4 w-4 text-blue-500" />
              {t('modal.timeLabel')}
            </label>
            <input type="time" value={appointmentForm.time} onChange={(e) => handleTimeChange(e.target.value)}
              className={`w-full px-3 py-2 border ${timeError ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`} />
            {timeError && (
              <div className="text-red-500 text-xs flex items-center mt-1">
                <AlertCircle className="h-3 w-3 mr-1" /> {timeError}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <User className="h-4 w-4 text-blue-500" />
              {t('modal.customerLabel')}
            </label>
            <SearchableDropdown
              items={customers}
              onSelect={(customer) => setAppointmentForm((prev) => ({ ...prev, customerId: customer.id, selectedCustomer: customer }))}
              placeholder={t('modal.customerPlaceholder')}
              value={appointmentForm.selectedCustomer}
              icon={User}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Briefcase className="h-4 w-4 text-blue-500" />
              {t('modal.employeeLabel')}
            </label>
            <SearchableDropdown
              items={employees}
              onSelect={(employee) => setAppointmentForm((prev) => ({ ...prev, employeeId: employee.id, selectedEmployee: employee }))}
              placeholder={t('modal.employeePlaceholder')}
              value={appointmentForm.selectedEmployee}
              icon={Briefcase}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText className="h-4 w-4 text-blue-500" />
              {t('modal.servicesLabel')}
            </label>
            <MultiServiceSelector
              services={services}
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              resetAfterSelect={true}
              maxServices={10}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <AlertCircle className="h-4 w-4 text-blue-500" />
              {t('modal.statusLabel')}
            </label>
            <select value={appointmentForm.status}
              onChange={(e) => setAppointmentForm((prev) => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
              <option value="approved">{t('modal.statusApproved')}</option>
              <option value="pending">{t('modal.statusPending')}</option>
              <option value="completed">{t('modal.statusCompleted')}</option>
              <option value="canceled">{t('modal.statusCanceled')}</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText className="h-4 w-4 text-blue-500" />
              {t('modal.paymentLabel')}
            </label>
            <select value={appointmentForm.paymentMethod || 'Nakit'}
              onChange={(e) => setAppointmentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
              <option value="Nakit">{t('modal.paymentCash')}</option>
              <option value="Kredi Kartı">{t('modal.paymentCard')}</option>
              <option value="Banka Havalesi">{t('modal.paymentTransfer')}</option>
              <option value="Diğer">{t('modal.paymentOther')}</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText className="h-4 w-4 text-blue-500" />
              {t('modal.notesLabel')}
            </label>
            <textarea value={appointmentForm.notes || ''}
              onChange={(e) => setAppointmentForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder={t('modal.notesPlaceholder')} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
          </div>

          {selectedServices.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-lg font-medium text-gray-900 mb-4 flex items-center justify-between">
                <span>{t('modal.serviceTotalLabel')}</span>
                <span>{selectedServices.reduce((sum, s) => sum + Number(s.price || 0), 0)} TL</span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
          <button onClick={onClose} disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {t('modal.cancelBtn')}
          </button>
          <button onClick={onSave}
            disabled={isSaving || !appointmentForm.date || !appointmentForm.time || !appointmentForm.customerId || !appointmentForm.employeeId || selectedServices.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-md transition-all disabled:opacity-70 disabled:cursor-not-allowed">
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? t('modal.savingBtn') : (isEditing ? t('modal.updateBtn') : t('modal.saveBtn'))}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AppointmentModal;
